/**
 * Authentication service for the Word add-in.
 *
 * Wraps the Office.js Dialog API to open a login popup and handle
 * the messageParent callback. Communicates with the API gateway
 * for authorize URL generation and dev-mode login.
 *
 * @module auth/auth-service
 */

import type {
  AuthSession,
  AuthorizeUrlResponse,
  AuthCallbackMessage,
} from '@alterspective/shared';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const DEFAULT_GATEWAY_URL = 'https://localhost:3001';
const DEFAULT_DEMO_INSTANCE_URL =
  (typeof __DEMO_INSTANCE_URL__ !== 'undefined' ? __DEMO_INSTANCE_URL__ : undefined) ?? '';
const DEFAULT_DEMO_MATTER_ID =
  (typeof __DEMO_MATTER_ID__ !== 'undefined' ? __DEMO_MATTER_ID__ : undefined) ?? '';

// ---------------------------------------------------------------------------
// Auth service
// ---------------------------------------------------------------------------

/**
 * Get the authorize URL from the gateway for a given PMS instance.
 */
export async function getAuthorizeUrl(
  gatewayUrl: string,
  instanceUrl: string,
): Promise<AuthorizeUrlResponse> {
  const url = `${gatewayUrl}/auth/authorize-url?instanceUrl=${encodeURIComponent(instanceUrl)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Failed to get authorize URL: ${response.status}`);
  }

  return (await response.json()) as AuthorizeUrlResponse;
}

/**
 * Open the login dialog using the Office.js Dialog API.
 *
 * The dialog navigates through the identity server and back to the gateway
 * callback page, which completes the code exchange server-side.
 * The task pane polls GET /auth/session/:state until the session is ready,
 * then closes the dialog. This avoids relying on Office.js messageParent
 * across cross-origin redirects, which is unreliable in desktop Word.
 */
export function openLoginDialog(
  gatewayUrl: string,
  authorizeUrl: string,
  state: string,
): Promise<AuthSession> {
  return new Promise((resolve, reject) => {
    const startUrl = `${gatewayUrl}/auth/start?url=${encodeURIComponent(authorizeUrl)}`;

    if (typeof Office === 'undefined' || !Office.context?.ui?.displayDialogAsync) {
      reject(new Error('Office.js Dialog API not available'));
      return;
    }

    Office.context.ui.displayDialogAsync(
      startUrl,
      { height: 60, width: 40, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Dialog failed: ${asyncResult.error.message}`));
          return;
        }

        const dialog = asyncResult.value;
        let settled = false;

        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearInterval(pollInterval);
          fn();
        };

        // Poll the gateway for a completed session every second
        const pollInterval = setInterval(async () => {
          try {
            const resp = await fetch(`${gatewayUrl}/auth/session/${encodeURIComponent(state)}`);
            if (resp.status === 200) {
              const session = (await resp.json()) as AuthSession;
              dialog.close();
              settle(() => resolve(session));
            }
            // 202 = still pending, keep polling
          } catch {
            // network blip — keep polling
          }
        }, 1000);

        // Handle dialog closed by user before sign-in completes
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (args) => {
            const eventArgs = args as { error?: number };
            settle(() => {
              if (eventArgs.error === 12006) {
                reject(new Error('Sign-in cancelled'));
              } else {
                reject(new Error(`Dialog event error: ${eventArgs.error}`));
              }
            });
          },
        );
      },
    );
  });
}

/**
 * Dev-mode login: authenticate using client_credentials via the gateway.
 */
export async function devLogin(
  gatewayUrl: string,
  instanceUrl: string,
): Promise<AuthSession> {
  const response = await fetch(`${gatewayUrl}/auth/dev-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instanceUrl }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Dev login failed: ${response.status}`);
  }

  return (await response.json()) as AuthSession;
}

/**
 * Logout: destroy the session on the gateway.
 */
export async function logout(
  gatewayUrl: string,
  sessionToken: string,
): Promise<void> {
  await fetch(`${gatewayUrl}/auth/logout`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });
}

/**
 * Get the gateway URL (from environment or default).
 */
// Only accept ?gateway= values that point to Alterspective-owned origins.
// Prevents an attacker from crafting a link that redirects session tokens to an
// arbitrary server (open-redirect / credential-exfiltration via the query param).
const GATEWAY_ALLOWLIST = /^https:\/\/[\w-]+\.alterspective\.com\.au(:\d+)?$/;
const LOCAL_GATEWAY_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/** Return true when dev-only auth affordances may be shown for the gateway. */
export function canUseDevLogin(gatewayUrl: string): boolean {
  try {
    const url = new URL(gatewayUrl);
    return (url.protocol === 'http:' || url.protocol === 'https:')
      && LOCAL_GATEWAY_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

export function getGatewayUrl(): string {
  // Runtime override via ?gateway= query param — allows a single shared static
  // deployment to serve multiple clients, each with their own gateway instance.
  const param = new URLSearchParams(window.location.search).get('gateway');
  if (param) {
    const clean = param.replace(/\/$/, '');
    if (GATEWAY_ALLOWLIST.test(clean)) return clean;
  }

  return (
    (typeof __GATEWAY_URL__ !== 'undefined' ? __GATEWAY_URL__ : undefined) ??
    DEFAULT_GATEWAY_URL
  );
}

/**
 * Get the optional demo instance URL injected by webpack.
 */
export function getDemoInstanceUrl(): string {
  return DEFAULT_DEMO_INSTANCE_URL;
}

/**
 * Get the optional demo matter ID injected by webpack.
 */
export function getDemoMatterId(): string {
  return DEFAULT_DEMO_MATTER_ID;
}

// Webpack DefinePlugin global
declare const __GATEWAY_URL__: string | undefined;
declare const __DEMO_INSTANCE_URL__: string | undefined;
declare const __DEMO_MATTER_ID__: string | undefined;
